import React, { useState, useEffect, useMemo, useCallback } from 'react';
import Row from '@amzn/meridian/row';
import Column from '@amzn/meridian/column';
import Select, { SelectOption } from '@amzn/meridian/select';
import Input from '@amzn/meridian/input';
import Button from '@amzn/meridian/button';
import Alert from '@amzn/meridian/alert';
import Loader from '@amzn/meridian/loader';
import Text from '@amzn/meridian/text';
import Checkbox from '@amzn/meridian/checkbox';
import DataTable, {
    DataTableBody,
    DataTableHeader,
    DataTableHead,
    DataTableCell,
    DataTableRow,
} from '@amzn/meridian/data-table';
import Divider from '@amzn/meridian/divider';
import Pagination from '@amzn/meridian/pagination';
import { getDSData, getDSDataPaginated, bulkUpdateDSData, bulkUpdateDSDataInBatches, approveTacticalDeployment } from '../services/api';
import { TACTICAL_CAPS_TABLE } from '../appConfig';
import { useAuth } from '../context/AuthContext';
import { usePermissions } from '../context/PermissionsContext';
import { today, tacticalCapsDateOptions } from '../utils/dateUtils';
import { parseCSV, downloadCSV } from '../utils/csvUtils';
import FileUploadModal, { FileFieldConfig } from '../components/FileUploadModal';

interface TacticalCapItem {
    ofd_date: string;
    node_type: string;
    location_id: string;
    ship_method?: string;
    cycle_name: string;
    cap_interval_start: string;
    cap_interval_end: string;
    internal_sort_code: string;
    deployed_caps: string;
    base_value?: string;
    co_proposed_caps?: string;
    ds_input?: string;
    reason?: string;
    cap_deployment_status?: string;
    delivery_station?: string;
    cpt?: string;
    last_updated_at?: string;
    last_updated_by?: string;
}


const INCREASE_REASONS = [
    { value: 'Capacity OTR- DSP', label: 'Capacity OTR- DSP' },
    { value: 'Capacity OTR- Flex', label: 'Capacity OTR- Flex' },
    { value: 'Capacity UTR', label: 'Capacity UTR' },
    { value: '__OTHER__', label: 'Other (enter reason below)' },
];

const DECREASE_REASONS = [
    { value: 'Capacity UTR - Sudden Absenteeism', label: 'Capacity UTR - Sudden Absenteeism' },
    { value: 'Capacity OTR - DSP Sudden Absenteeism', label: 'Capacity OTR - DSP Sudden Absenteeism' },
    { value: 'Capacity OTR - FLEX Cancel/No Show', label: 'Capacity OTR - FLEX Cancel/No Show' },
    { value: 'MNR Line Haul Delay', label: 'MNR Line Haul Delay' },
    { value: 'Weather Impact Reasons', label: 'Weather Impact Reasons' },
    { value: 'High RTS', label: 'High RTS' },
    { value: 'High Schedul', label: 'High Schedule' },
    { value: 'SPR Forecast Issue', label: 'SPR Forecast Issue' },
    { value: 'MCV Constraints', label: 'MCV Constraints' },
    { value: '__OTHER__', label: 'Other (enter reason below)' },
];

function validateDSInput(item: TacticalCapItem, newValue: string): { isValid: boolean; error?: string } {
    return { isValid: true };
}

function extractTime(isoString: string): string {
    if (!isoString) return '';
    const match = isoString.match(/T(\d{2}:\d{2})/);
    return match ? match[1] : '';
}

const COLUMNS = [
    { id: 'ofd_date', label: 'OFD Date' },
    { id: 'location_id', label: 'Node' },
    { id: 'ship_method', label: 'Ship Method' },
    { id: 'cpt', label: 'CPT' },
    { id: 'internal_sort_code', label: 'Sort Code' },
    { id: 'deployed_caps', label: 'Deployed Caps' },
    { id: 'co_proposed_caps', label: 'CO Proposed Caps' },
    { id: 'ds_input', label: 'DS Input' },
    { id: 'reason', label: 'Reason' },
    { id: 'cap_deployment_status', label: 'Cap Deployment Status' },
    { id: 'last_updated_at', label: 'Last Updated At' },
    { id: 'last_updated_by', label: 'Last Updated By' },
];

const TacticalCaps: React.FC = () => {
    const { user } = useAuth();
    const { hasFullAccess } = usePermissions();
    const [data, setData] = useState<TacticalCapItem[]>([]);
    const [isLoading, setIsLoading] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const [selectedDate, setSelectedDate] = useState<string>('');
    const [selectedNode, setSelectedNode] = useState<string>('');
    const [selectedShipMethod, setSelectedShipMethod] = useState<string>('');
    const [selectedCpt, setSelectedCpt] = useState<string>('');
    const [selectedSortCode, setSelectedSortCode] = useState<string>('');
    const [currentPage, setCurrentPage] = useState(1);
    const [resultsPerPage, setResultsPerPage] = useState(20);
    const [sortColumn, setSortColumn] = useState('ofd_date');
    const [sortDirection, setSortDirection] = useState<'ascending' | 'descending'>('ascending');
    const [editingRows, setEditingRows] = useState<Record<string, { dsInput: string; reason: string; otherReason: string }>>({});
    const [savingRows, setSavingRows] = useState<Record<string, boolean>>({});
    const [validationError, setValidationError] = useState<string | null>(null);
    const [isUploadModalOpen, setIsUploadModalOpen] = useState(false);
    const [uploadProgress, setUploadProgress] = useState<{ completed: number; total: number } | null>(null);
    const [selectedRows, setSelectedRows] = useState<Set<string>>(new Set());
    const [isApproving, setIsApproving] = useState(false);
    const [isRejecting, setIsRejecting] = useState(false);

    useEffect(() => { loadData(); }, []);
    useEffect(() => { setCurrentPage(1); setSelectedRows(new Set()); }, [selectedDate, selectedNode, selectedShipMethod, selectedCpt, selectedSortCode, resultsPerPage]);

    // Reset child filters when parent filters change
    useEffect(() => {
        setSelectedShipMethod('');
        setSelectedCpt('');
        setSelectedSortCode('');
        setSelectedRows(new Set());
    }, [selectedDate, selectedNode]);

    useEffect(() => {
        setSelectedCpt('');
        setSelectedSortCode('');
        setSelectedRows(new Set());
    }, [selectedShipMethod]);

    useEffect(() => {
        setSelectedSortCode('');
        setSelectedRows(new Set());
    }, [selectedCpt]);

    const loadData = useCallback(async () => {
        setIsLoading(true);
        setError(null);
        try {
            const items = await getDSDataPaginated(TACTICAL_CAPS_TABLE, {
                filters: [
                    { key: 'ofd_date', operator: 'in', values: tacticalCapsDateOptions() },
                ],
            });
            setData(items);
        } catch (err: any) {
            setError(err.message || 'Failed to load tactical caps data');
        } finally {
            setIsLoading(false);
        }
    }, []);

    // Derive dropdown options from data
    const dateOptions = useMemo(() => {
        return Array.from(new Set(data.map(item => item.ofd_date))).filter(Boolean).sort();
    }, [data]);

    const nodeOptions = useMemo(() => {
        let filtered = data;
        if (selectedDate) filtered = filtered.filter(item => item.ofd_date === selectedDate);
        return Array.from(new Set(filtered.map(item => item.location_id))).filter(Boolean).sort();
    }, [data, selectedDate]);

    const shipMethodOptions = useMemo(() => {
        let filtered = data;
        if (selectedDate) filtered = filtered.filter(item => item.ofd_date === selectedDate);
        if (selectedNode) filtered = filtered.filter(item => item.location_id === selectedNode);
        return Array.from(new Set(filtered.map(item => item.ship_method || ''))).filter(Boolean).sort();
    }, [data, selectedDate, selectedNode]);

    const cptOptions = useMemo(() => {
        let filtered = data;
        if (selectedDate) filtered = filtered.filter(item => item.ofd_date === selectedDate);
        if (selectedNode) filtered = filtered.filter(item => item.location_id === selectedNode);
        if (selectedShipMethod) filtered = filtered.filter(item => item.ship_method === selectedShipMethod);
        return Array.from(new Set(filtered.map(item => item.cpt || ''))).filter(Boolean).sort();
    }, [data, selectedDate, selectedNode, selectedShipMethod]);

    const sortCodeOptions = useMemo(() => {
        let filtered = data;
        if (selectedDate) filtered = filtered.filter(item => item.ofd_date === selectedDate);
        if (selectedNode) filtered = filtered.filter(item => item.location_id === selectedNode);
        if (selectedShipMethod) filtered = filtered.filter(item => item.ship_method === selectedShipMethod);
        if (selectedCpt) filtered = filtered.filter(item => item.cpt === selectedCpt);
        const values = Array.from(new Set(filtered.map(item => item.internal_sort_code || '')));
        const nonEmpty = values.filter(Boolean).sort();
        if (values.includes('')) nonEmpty.push('__BLANK__');
        return nonEmpty;
    }, [data, selectedDate, selectedNode, selectedShipMethod, selectedCpt]);

    const filteredData = useMemo(() => {
        let result = [...data];
        if (selectedDate) result = result.filter(item => item.ofd_date === selectedDate);
        if (selectedNode) result = result.filter(item => item.location_id === selectedNode);
        if (selectedShipMethod) result = result.filter(item => item.ship_method === selectedShipMethod);
        if (selectedCpt) result = result.filter(item => item.cpt === selectedCpt);
        if (selectedSortCode) {
            result = selectedSortCode === '__BLANK__'
                ? result.filter(item => !item.internal_sort_code)
                : result.filter(item => item.internal_sort_code === selectedSortCode);
        }
        result.sort((a, b) => {
            const aVal = (a as any)[sortColumn] || '';
            const bVal = (b as any)[sortColumn] || '';
            const cmp = aVal.localeCompare(bVal, undefined, { numeric: true });
            return sortDirection === 'ascending' ? cmp : -cmp;
        });
        return result;
    }, [data, selectedDate, selectedNode, selectedShipMethod, selectedCpt, selectedSortCode, sortColumn, sortDirection]);

    const totalPages = Math.max(1, Math.ceil(filteredData.length / resultsPerPage));
    const paginatedData = filteredData.slice((currentPage - 1) * resultsPerPage, currentPage * resultsPerPage);

    const onSort = useCallback(({ sortColumn: col, sortDirection: dir }: any) => {
        setSortColumn(col);
        setSortDirection(dir);
    }, []);

    const handleEditStart = (item: TacticalCapItem) => {
        const key = getRowKey(item);
        setEditingRows(prev => ({
            ...prev,
            [key]: { dsInput: item.ds_input || '', reason: item.reason || '', otherReason: '' },
        }));
    };

    const handleEditCancel = (item: TacticalCapItem) => {
        const key = getRowKey(item);
        setEditingRows(prev => {
            const updated = { ...prev };
            delete updated[key];
            return updated;
        });
    };

    const handleEditSave = async (item: TacticalCapItem) => {
        const key = getRowKey(item);
        const editing = editingRows[key];
        if (!editing) return;

        const { dsInput, reason, otherReason } = editing;

        // Validate DS Input is not empty
        if (!dsInput || dsInput.trim() === '') {
            setValidationError('DS Input is required.');
            return;
        }

        // Determine final reason
        const finalReason = reason === '__OTHER__' ? otherReason : reason;
        if (!finalReason || finalReason.trim() === '') {
            setValidationError('Reason is required.');
            return;
        }

        // Validate DS Input value
        const validation = validateDSInput(item, dsInput);
        if (!validation.isValid) {
            setValidationError(validation.error || 'Validation failed');
            return;
        }

        setSavingRows(prev => ({ ...prev, [key]: true }));
        try {
            const updates: Record<string, string> = { ds_input: dsInput, reason: finalReason };

            // Set base_value if DS Input is being set for the first time
            const currentDsInput = item.ds_input;
            if ((!currentDsInput || currentDsInput === '') && dsInput && item.deployed_caps) {
                updates.base_value = item.deployed_caps;
            }

            // Clear cap_deployment_status if re-entering DS input after rejection
            const currentStatus = (item.cap_deployment_status || '').trim();
            if (currentStatus === 'Rejected') {
                updates.cap_deployment_status = '';
            }

            await bulkUpdateDSData(TACTICAL_CAPS_TABLE, [{
                ofd_date: item.ofd_date,
                location_id: item.location_id,
                cycle_name: item.cycle_name,
                internal_sort_code: item.internal_sort_code,
                cpt: item.cpt,
                ship_method: item.ship_method,
                updates,
            }]);

            setEditingRows(prev => {
                const updated = { ...prev };
                delete updated[key];
                return updated;
            });
            await loadData();
        } catch (err: any) {
            setError(err.message || 'Failed to save DS Input');
        } finally {
            setSavingRows(prev => ({ ...prev, [key]: false }));
        }
    };

    const getReasonOptions = (item: TacticalCapItem, dsInputValue: string) => {
        const dsInput = parseFloat(dsInputValue);
        const atropsValue = parseFloat(item.deployed_caps || '');
        if (isNaN(dsInput) || isNaN(atropsValue)) return [];
        if (dsInput > atropsValue) return INCREASE_REASONS;
        if (dsInput < atropsValue) return DECREASE_REASONS;
        return [];
    };

    const handleUpload = () => {
        setIsUploadModalOpen(true);
    };

    const UPLOAD_FIELDS: FileFieldConfig[] = [
        { key: 'tactical_caps_csv', label: 'Tactical Caps CSV', required: true, accept: '.csv' },
    ];


    const handleUploadSubmit = async (files: Record<string, File>) => {
        const file = files['tactical_caps_csv'];
        if (!file) return;

        const text = await file.text();
        const rows = parseCSV(text);

        if (rows.length === 0) {
            setError('CSV file is empty or has no data rows.');
            setIsUploadModalOpen(false);
            return;
        }

        const updates = rows
            .map(row => {
                const coProposedCaps = row.co_proposed_caps || row.co_proposed_capacity || row['CO Proposed Caps'] || '';
                return {
                    ofd_date: row.ofd_date || row['OFD Date'] || '',
                    location_id: row.location_id || row['Node'] || '',
                    cycle_name: row.cycle_name || row['Cycle Name'] || '',
                    internal_sort_code: row.internal_sort_code || row['Sort Code'] || '',
                    cpt: row.cpt || row['CPT'] || '',
                    ship_method: row.ship_method || row['Ship Method'] || '',
                    updates: {
                        co_proposed_caps: coProposedCaps,
                    },
                };
            })
            .filter(row => row.ofd_date && row.location_id && row.updates.co_proposed_caps);

        if (updates.length === 0) {
            setError('No valid rows found with CO Proposed Caps values.');
            setIsUploadModalOpen(false);
            return;
        }

        setIsUploadModalOpen(false);
        setUploadProgress({ completed: 0, total: updates.length });

        try {
            const result = await bulkUpdateDSDataInBatches(TACTICAL_CAPS_TABLE, updates, {
                batchSize: 25,
                onProgress: (completed, total) => {
                    setUploadProgress({ completed, total });
                },
            });

            setUploadProgress(null);

            if (result.failedBatches > 0) {
                setError(`Upload partially completed: ${result.successCount} records updated, ${result.failedBatches} batch(es) failed.`);
            }

            await loadData();
        } catch (err: any) {
            setUploadProgress(null);
            setError(err.message || 'Failed to upload tactical caps data');
        }
    };

    const buildCompositeKey = (item: TacticalCapItem): string => {
        const ofdDate = (item.ofd_date || '').replace(/\//g, '-');
        return `${ofdDate}#${item.location_id}#${item.internal_sort_code || ''}#${item.cpt || ''}#${item.ship_method || ''}`;
    };

    const handleApproveSelected = async () => {
        if (selectedRows.size === 0) return;

        setIsApproving(true);
        setError(null);

        const itemsToApprove: TacticalCapItem[] = [];
        filteredData.forEach((item) => {
            const key = getRowKey(item);
            if (selectedRows.has(key)) {
                itemsToApprove.push(item);
            }
        });

        if (itemsToApprove.length === 0) {
            setIsApproving(false);
            return;
        }

        const compositeKeys = itemsToApprove.map(item => buildCompositeKey(item));

        try {
            await approveTacticalDeployment(user || '', compositeKeys);
            setSelectedRows(new Set());
            await loadData();
        } catch (err: any) {
            setError(err.message || 'Failed to approve selected records');
        } finally {
            setIsApproving(false);
        }
    };

    const getCellValue = (item: any, colId: string): string => {
        return item[colId] || '';
    };

    const handleDownload = () => {
        const headers = COLUMNS.map(col => col.label);
        const rows = filteredData.map(item =>
            COLUMNS.map(col => getCellValue(item, col.id))
        );
        downloadCSV(`tactical_caps_${today()}.csv`, headers, rows);
    };

    const getRowKey = (item: TacticalCapItem) =>
        `${item.ofd_date}|${item.location_id}|${item.cycle_name}|${item.internal_sort_code || ''}|${item.cpt || ''}|${item.ship_method || ''}`;

    // Row selection helpers for reject feature
    const isRowEditable = (item: TacticalCapItem) => {
        const status = (item.cap_deployment_status || '').trim();
        return !status || status === '' || status === 'Success' || status === 'Failed' || status === 'Initiated' || status === 'Rejected';
    };

    const isRowSelectable = (item: TacticalCapItem) => {
        const status = (item.cap_deployment_status || '').trim();
        return status === 'Initiated';
    };

    const selectableRowsOnPage = useMemo(() => {
        return paginatedData
            .map((item, idx) => ({ item, key: getRowKey(item) }))
            .filter(({ item }) => isRowSelectable(item));
    }, [paginatedData]);

    const allPageSelected = selectableRowsOnPage.length > 0 && selectableRowsOnPage.every(({ key }) => selectedRows.has(key));

    const handleSelectAll = (checked: boolean) => {
        setSelectedRows(prev => {
            const next = new Set(prev);
            selectableRowsOnPage.forEach(({ key }) => {
                if (checked) next.add(key);
                else next.delete(key);
            });
            return next;
        });
    };

    const handleRowSelect = (key: string, checked: boolean) => {
        setSelectedRows(prev => {
            const next = new Set(prev);
            if (checked) next.add(key);
            else next.delete(key);
            return next;
        });
    };

    const handleRejectSelected = async () => {
        if (selectedRows.size === 0) return;

        setIsRejecting(true);
        setError(null);

        // Find the actual items to reject from filteredData
        const itemsToReject: TacticalCapItem[] = [];
        filteredData.forEach((item, idx) => {
            const key = getRowKey(item);
            if (selectedRows.has(key)) {
                itemsToReject.push(item);
            }
        });

        if (itemsToReject.length === 0) {
            setIsRejecting(false);
            return;
        }

        const records = itemsToReject.map(item => ({
            ofd_date: item.ofd_date,
            location_id: item.location_id,
            cycle_name: item.cycle_name,
            internal_sort_code: item.internal_sort_code,
            cpt: item.cpt,
            ship_method: item.ship_method,
            updates: {
                ds_input: '',
                reason: '',
                cap_deployment_status: 'Rejected',
            },
        }));

        try {
            await bulkUpdateDSDataInBatches(TACTICAL_CAPS_TABLE, records, {
                batchSize: 25,
            });
            setSelectedRows(new Set());
            await loadData();
        } catch (err: any) {
            setError(err.message || 'Failed to reject selected DS inputs');
        } finally {
            setIsRejecting(false);
        }
    };

    return (
        <Column spacing="400">
            <FileUploadModal
                open={isUploadModalOpen}
                onClose={() => setIsUploadModalOpen(false)}
                onSubmit={handleUploadSubmit}
                title="Upload Tactical Caps"
                fields={UPLOAD_FIELDS}
                submitLabel="Upload"
                loadingLabel="Uploading..."
            />

            {error && (
                <Alert type="error" size="medium" onClose={() => setError(null)}>
                    {error}
                </Alert>
            )}

            {validationError && (
                <Alert type="warning" size="medium" onClose={() => setValidationError(null)}>
                    {validationError}
                </Alert>
            )}

            {uploadProgress && (
                <Alert type="neutral" size="medium">
                    <Row spacing="300" alignmentVertical="center">
                        <Loader size="small" />
                        <Text>
                            Uploading records: {uploadProgress.completed} / {uploadProgress.total} ({Math.round((uploadProgress.completed / uploadProgress.total) * 100)}%)
                        </Text>
                    </Row>
                </Alert>
            )}

            {isLoading && (
                <Row alignmentHorizontal="center" spacingInset="450">
                    <Loader size="small" />
                </Row>
            )}

            {!isLoading && (
                <Column width="100%">
                    {/* Action Bar */}
                    <Column type="fill" spacing="300">
                        <Row
                            spacingInset="300 none"
                            width="100%"
                            widths="fit"
                            alignmentHorizontal="justify"
                        >
                            <Row spacing="200" spacingInset="none 300 none">
                                <Select
                                    value={selectedDate}
                                    onChange={setSelectedDate}
                                    placeholder="OFD Date"
                                    size="small"
                                    width={150}
                                >
                                    {dateOptions.map(date => (
                                        <SelectOption key={date} value={date} label={date} />
                                    ))}
                                </Select>
                                <Select
                                    value={selectedNode}
                                    onChange={setSelectedNode}
                                    placeholder="Node"
                                    size="small"
                                    width={150}
                                >
                                    {nodeOptions.map(node => (
                                        <SelectOption key={node} value={node} label={node} />
                                    ))}
                                </Select>
                                <Select
                                    value={selectedShipMethod}
                                    onChange={setSelectedShipMethod}
                                    placeholder="Ship Method"
                                    size="small"
                                    width={150}
                                >
                                    {shipMethodOptions.map(method => (
                                        <SelectOption key={method} value={method} label={method} />
                                    ))}
                                </Select>
                                <Select
                                    value={selectedCpt}
                                    onChange={setSelectedCpt}
                                    placeholder="CPT"
                                    size="small"
                                    width={150}
                                >
                                    {cptOptions.map(cpt => (
                                        <SelectOption key={cpt} value={cpt} label={cpt} />
                                    ))}
                                </Select>
                                <Select
                                    value={selectedSortCode}
                                    onChange={setSelectedSortCode}
                                    placeholder="Sort Code"
                                    size="small"
                                    width={150}
                                >
                                    {sortCodeOptions.map(code => (
                                        <SelectOption key={code} value={code} label={code === '__BLANK__' ? '(Blank)' : code} />
                                    ))}
                                </Select>
                            </Row>
                            <Row spacing="200" spacingInset="none 300 none">
                                {hasFullAccess && (
                                    <Button type="secondary" size="small" onClick={handleUpload} disabled={!!uploadProgress}>
                                        Upload
                                    </Button>
                                )}
                                {hasFullAccess && (
                                    <Button
                                        type="primary"
                                        size="small"
                                        onClick={handleApproveSelected}
                                        disabled={selectedRows.size === 0 || isApproving || isRejecting}
                                    >
                                        {isApproving ? 'Approving...' : `Approve Selected (${selectedRows.size})`}
                                    </Button>
                                )}
                                {hasFullAccess && (
                                    <Button
                                        type="secondary"
                                        size="small"
                                        onClick={handleRejectSelected}
                                        disabled={selectedRows.size === 0 || isApproving || isRejecting}
                                    >
                                        {isRejecting ? 'Rejecting...' : `Reject Selected (${selectedRows.size})`}
                                    </Button>
                                )}
                                <Button type="secondary" size="small" onClick={handleDownload}>
                                    Download
                                </Button>
                                <Button type="secondary" size="small" onClick={() => { setSelectedDate(''); setSelectedNode(''); setSelectedShipMethod(''); setSelectedCpt(''); setSelectedSortCode(''); loadData(); }} disabled={isLoading}>
                                    Refresh
                                </Button>
                            </Row>
                        </Row>
                        <Divider />
                    </Column>

                    {/* Data Table */}
                    <DataTable
                        onSort={onSort}
                        sortColumn={sortColumn}
                        sortDirection={sortDirection}
                    >
                        <DataTableHead>
                            <DataTableRow highlightOnHover={false}>
                                {hasFullAccess && (
                                    <DataTableHeader key="__select__">
                                        <Checkbox
                                            checked={allPageSelected}
                                            onChange={handleSelectAll}
                                            disabled={selectableRowsOnPage.length === 0 || isApproving || isRejecting}
                                        />
                                    </DataTableHeader>
                                )}
                                {COLUMNS.map((col: { id: string; label: string }) => (
                                    <DataTableHeader key={col.id} sortColumn={col.id}>{col.label}</DataTableHeader>
                                ))}
                            </DataTableRow>
                        </DataTableHead>
                        <DataTableBody>
                            {paginatedData.map((item, idx) => (
                                <DataTableRow key={getRowKey(item)} highlightOnHover={false}>
                                    {hasFullAccess && (
                                        <DataTableCell>
                                            {isRowSelectable(item) ? (
                                                <Checkbox
                                                    checked={selectedRows.has(getRowKey(item))}
                                                    onChange={(checked: boolean) => handleRowSelect(getRowKey(item), checked)}
                                                    disabled={isApproving || isRejecting}
                                                />
                                            ) : null}
                                        </DataTableCell>
                                    )}
                                    <DataTableCell>{item.ofd_date}</DataTableCell>
                                    <DataTableCell>{item.location_id}</DataTableCell>
                                    <DataTableCell>{item.ship_method || ''}</DataTableCell>
                                    <DataTableCell>{item.cpt || ''}</DataTableCell>
                                    <DataTableCell>{item.internal_sort_code}</DataTableCell>
                                    <DataTableCell>{item.deployed_caps}</DataTableCell>
                                    <DataTableCell>{item.co_proposed_caps || ''}</DataTableCell>
                                    <DataTableCell>
                                        {(() => {
                                            const editable = isRowEditable(item);
                                            if (editingRows[getRowKey(item)] && editable) {
                                                return (
                                                    <Row spacing="100" alignmentVertical="center">
                                                        <Input
                                                            value={editingRows[getRowKey(item)].dsInput}
                                                            onChange={(val: string) => setEditingRows(prev => ({
                                                                ...prev,
                                                                [getRowKey(item)]: { ...prev[getRowKey(item)], dsInput: val },
                                                            }))}
                                                            size="small"
                                                            type="text"
                                                        />
                                                        <Button
                                                            type="primary"
                                                            size="small"
                                                            onClick={() => handleEditSave(item)}
                                                            disabled={savingRows[getRowKey(item)]}
                                                        >
                                                            {savingRows[getRowKey(item)] ? '...' : '✓'}
                                                        </Button>
                                                        <Button
                                                            type="secondary"
                                                            size="small"
                                                            onClick={() => handleEditCancel(item)}
                                                            disabled={savingRows[getRowKey(item)]}
                                                        >
                                                            ✕
                                                        </Button>
                                                    </Row>
                                                );
                                            }
                                            return (
                                                <Row spacing="100" alignmentVertical="center">
                                                    <Text>{item.ds_input || ''}</Text>
                                                    {editable && (
                                                        <Button type="icon" size="small" onClick={() => handleEditStart(item)}>✎</Button>
                                                    )}
                                                </Row>
                                            );
                                        })()}
                                    </DataTableCell>
                                    <DataTableCell>
                                        {(() => {
                                            const editable = isRowEditable(item);
                                            if (editingRows[getRowKey(item)] && editable) {
                                                return (
                                                    <Column spacing="100">
                                                        <Select
                                                            value={editingRows[getRowKey(item)].reason}
                                                            onChange={(val) => setEditingRows(prev => ({
                                                                ...prev,
                                                                [getRowKey(item)]: { ...prev[getRowKey(item)], reason: val, otherReason: val !== '__OTHER__' ? '' : prev[getRowKey(item)].otherReason },
                                                            }))}
                                                            size="small"
                                                            placeholder="Select reason"
                                                        >
                                                            {getReasonOptions(item, editingRows[getRowKey(item)].dsInput).map(opt => (
                                                                <SelectOption key={opt.value} value={opt.value} label={opt.label} />
                                                            ))}
                                                        </Select>
                                                        {editingRows[getRowKey(item)].reason === '__OTHER__' && (
                                                            <Input
                                                                value={editingRows[getRowKey(item)].otherReason}
                                                                onChange={(val) => setEditingRows(prev => ({
                                                                    ...prev,
                                                                    [getRowKey(item)]: { ...prev[getRowKey(item)], otherReason: val },
                                                                }))}
                                                                size="small"
                                                                placeholder="Enter reason"
                                                            />
                                                        )}
                                                    </Column>
                                                );
                                            }
                                            return <Text>{item.reason || ''}</Text>;
                                        })()}
                                    </DataTableCell>
                                    <DataTableCell>{item.cap_deployment_status || ''}</DataTableCell>
                                    <DataTableCell>{item.last_updated_at || ''}</DataTableCell>
                                    <DataTableCell>{item.last_updated_by || ''}</DataTableCell>
                                </DataTableRow>
                            ))}
                            {paginatedData.length === 0 && (
                                <DataTableRow highlightOnHover={false}>
                                    <DataTableCell colSpan={COLUMNS.length + (hasFullAccess ? 1 : 0)}>
                                        <Row alignmentHorizontal="center" spacingInset="400">
                                            <Text color="secondary">No data available</Text>
                                        </Row>
                                    </DataTableCell>
                                </DataTableRow>
                            )}
                        </DataTableBody>
                    </DataTable>

                    {/* Table Footer */}
                    <Row
                        type="fill"
                        spacingInset="300"
                        width="100%"
                        widths="fit"
                        alignmentHorizontal="justify"
                    >
                        <Text type="b200" color="secondary">{filteredData.length} records</Text>
                        <Row spacing="200" spacingInset="none 300 none">
                            <Text tag="label" type="b200" color="primary" htmlFor="tactical-results-select">
                                Results per page
                            </Text>
                            <Select
                                id="tactical-results-select"
                                value={resultsPerPage}
                                onChange={setResultsPerPage}
                                size="small"
                            >
                                <SelectOption value={10} label="10" />
                                <SelectOption value={20} label="20" />
                                <SelectOption value={50} label="50" />
                            </Select>
                            {totalPages > 1 && (
                                <Pagination
                                    numberOfPages={totalPages}
                                    currentPage={currentPage}
                                    onChange={setCurrentPage}
                                />
                            )}
                        </Row>
                    </Row>
                </Column>
            )}
        </Column>
    );
};

export default TacticalCaps;
